import { FORNECEDORES } from '@/constants/Fornecedores'
import { Constants } from '@/services/constants'
import { EnumToList } from '.'
import { IFornecedor } from '@/interfaces/IFornecedor'
import { PRODUTOS_CREDITO_IMOBILIARIO } from '@/types/ProdutosCreditoImobiliario'
import { TIPO_IMOVEL } from '@/constants/TipoImovel'
import { RULES } from '@/constants/Rules'

export class FornecedorHelper {
	static get Lista() {
		return EnumToList(FORNECEDORES)
	}

	/**
	 * Get the name of the supplier
	 * @param fornecedorId
	 * @param produtoId
	 * @returns the name of the supplier
	 */
	static getNome(fornecedorId: number, produtoId?: number | undefined): string {
		switch (fornecedorId) {
			case Constants.BANCOS.ITAU:
				return 'itau'
			case Constants.BANCOS.BRADESCO:
				return 'bradesco'
			case Constants.BANCOS.SANTANDER:
				return 'santander'
			case Constants.BANCOS.CAIXA:
				return 'caixa'
		}

		return ''
	}

	/**
	 * Get the image path of the supplier
	 * @param fornecedorId
	 * @returns the image path of the supplier
	 */
	static getImagePathById(fornecedorId: FORNECEDORES) {
		return `/vectors/banks/${this.getNome(fornecedorId)}.svg`
	}

	/**
	 * Check if the supplier has an image
	 * @param fornecedorId
	 * @returns true if the supplier has an image
	 */
	static hasImage(fornecedorId: number) {
		switch (fornecedorId) {
			case FORNECEDORES.ITAU:
			case FORNECEDORES.BRADESCO:
			case FORNECEDORES.SANTANDER:
			case FORNECEDORES.CAIXA:
				return true
			default:
				return false
		}
	}

	/**
	 * Ge the bank name by id
	 * @param fornecedorId
	 * @returns the bank name
	 */
	static getBankNameById(fornecedorId: number) {
		switch (fornecedorId) {
			case FORNECEDORES.ITAU:
				return 'Itaú'
			case FORNECEDORES.BRADESCO:
				return 'Bradesco'
			case FORNECEDORES.SANTANDER:
				return 'Santander'
			case FORNECEDORES.CAIXA:
				return 'Caixa'
			default:
				return ''
		}
	}

	/**
	 * Get the minimum rate of the supplier
	 * @param fornecedorValoresList
	 * @param bancoId
	 * @param tipoProduto
	 * @param tipoImovel
	 * @param segmento
	 * @returns the minimum rate of the supplier
	 */
	static getTaxaMinima(
		fornecedorValoresList: Array<IFornecedor.IFornecedorValores>,
		bancoId: number,
		tipoImovel: number | null,
		tipoProduto: number | null = null,
		segmento: number | null = null
	): number {
		if (!tipoProduto) tipoProduto = PRODUTOS_CREDITO_IMOBILIARIO.SAC

		if (!tipoImovel) tipoImovel = Constants.TIPO_IMOVEL.RESIDENCIAL

		return this.getValorFornecedor(
			fornecedorValoresList,
			bancoId,
			Constants.FORNECEDOR_TIPO_PARAMETRO.TAXA_MINIMA,
			tipoImovel,
			tipoProduto,
			segmento
		)
	}

	/**
	 * Get the supplier value
	 * @param fornecedorValoresList
	 * @param bancoId
	 * @param tipoParametro
	 * @param tipoProduto
	 * @param tipoImovel
	 * @param segmento
	 * @returns the supplier value
	 */
	static getValorFornecedor(
		fornecedorValoresList: Array<IFornecedor.IFornecedorValores>,
		bancoId: number,
		tipoParametro: number,
		tipoImovel: number | null,
		tipoProduto: number | null = null,
		segmento: number | null = null
	): number {
		let result = fornecedorValoresList.find(
			(fornecedorValor) =>
				fornecedorValor.fornecedorId == bancoId &&
				fornecedorValor.tipoParametro == tipoParametro &&
				fornecedorValor.tipoProduto == tipoProduto &&
				fornecedorValor.tipoImovel == tipoImovel &&
				fornecedorValor.segmento == segmento
		)

		if (result) return result.valor

		result = fornecedorValoresList.find(
			(fornecedorValor) =>
				fornecedorValor.fornecedorId == bancoId &&
				fornecedorValor.tipoParametro == tipoParametro &&
				fornecedorValor.tipoImovel == tipoImovel
		)

		if (!result)
			result = {
				id: '',
				tipoParametro: 1,
				valor: 10,
				fornecedorId: 0,
				tipoProduto: 0,
				tipoImovel: 0,
				criadoEm: new Date(),
				desativadoEm: new Date(),
				segmento: null
			}

		return result.valor
	}

	/**
	 * If the supplier is allowed to be shown
	 * @param isFornecedorAllowed
	 * @param fornecedorId
	 * @param tipoImovel
	 * @returns true if the supplier is allowed to be shown     *
	 */
	static mostrarFornecedor(isFornecedorAllowed: boolean, fornecedorId: number, tipoImovel: number): boolean {
		if (!isFornecedorAllowed) return false

		//
		if (tipoImovel != TIPO_IMOVEL.RESIDENCIAL) {
			if (fornecedorId == FORNECEDORES.CAIXA) return false
		}

		return true
	}

	/**
	 * Get the minimum value between the suppliers
	 * @param fornecedorValoresList
	 * @param bancoIdList
	 * @param tipoParametro
	 * @param tipoProduto
	 * @param tipoImovel
	 * @param segmento
	 * @returns the minimum value between the suppliers
	 */
	static getValorMinimoEntreFornecedores(
		fornecedorValoresList: Array<IFornecedor.IFornecedorValores>,
		bancoIdList: Array<number>,
		tipoParametro: number,
		tipoImovel: number | null,
		tipoProduto: number | null = null,
		segmento: number | null = null
	): number {
		let valorMinimoAtual: number = 0,
			valorMinimo: number = 0

		//Pegar o menor valor entre os bancos selecionados
		bancoIdList.forEach((eachBanco) => {
			valorMinimoAtual = FornecedorHelper.getValorFornecedor(
				fornecedorValoresList,
				eachBanco,
				tipoParametro,
				tipoImovel,
				tipoProduto,
				segmento
			)

			if (valorMinimo == 0) valorMinimo = valorMinimoAtual

			if (valorMinimoAtual < valorMinimo) valorMinimo = valorMinimoAtual
		})

		return valorMinimo
	}

	/**
	 * Get the maximum value between the suppliers
	 * @param fornecedorValoresList
	 * @param bancoIdList
	 * @param tipoParametro
	 * @param tipoProduto
	 * @param tipoImovel
	 * @param segmento
	 * @returns the maximum value between the suppliers
	 */
	static getValorMaximoEntreFornecedores(
		fornecedorValoresList: Array<IFornecedor.IFornecedorValores>,
		bancoIdList: Array<number>,
		tipoParametro: number,
		tipoImovel: number | null,
		tipoProduto: number | null = null,
		segmento: number | null = null
	): number {
		let valorMaximoAtual: number = 0,
			valorMaximo: number = 0

		//Pegar o maior valor entre os bancos selecionados
		bancoIdList.forEach((eachBanco) => {
			valorMaximoAtual = FornecedorHelper.getValorFornecedor(
				fornecedorValoresList,
				eachBanco,
				tipoParametro,
				tipoImovel,
				tipoProduto,
				segmento
			)

			if (valorMaximo == 0) valorMaximo = valorMaximoAtual

			if (valorMaximoAtual > valorMaximo) valorMaximo = valorMaximoAtual
		})

		return valorMaximo
	}

	/**
	 * Check if the supplier is in the list
	 * @param fornecedoresList
	 * @param fornecedorId
	 * @returns true if the supplier is in the list
	 */
	static hasFornecedor(fornecedoresList: Array<FORNECEDORES>, fornecedorId: FORNECEDORES): boolean {
		if (!fornecedoresList) return false

		if (fornecedoresList.includes(fornecedorId)) return true

		return false
	}

	/**
	 * Check if the supplier is allowed
	 * @param fornecedorId
	 * @param userRules
	 * @returns true if the supplier is allowed
	 */
	static isFornecedorAllowed(fornecedorId: FORNECEDORES, userRules: Array<string>): boolean {
		switch (fornecedorId) {
			case FORNECEDORES.CAIXA:
				return userRules.includes(RULES.ISOLVE_GERAL_CAIXA)

			//Os outros bancos sao permitidos por padrao
			default:
				return true
		}
	}
}

export interface MostrarBanco {
	bancoId: number
	mostrar: boolean
}
